package com.evoapps.evoshift.spreadsheet

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.UploadFile
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.filedrop.FileDropMessage
import com.evoapps.evoshift.filedrop.FileDropSpec
import com.evoapps.evoshift.filedrop.fileDropTarget
import com.evoapps.evoshift.filedrop.rememberFileDropState
import com.evoapps.evoshift.filedrop.supportsFileDrop
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.Gold
import com.evoapps.evoshift.ui.GoldTintedWhite
import com.evoapps.evoshift.ui.util.asString
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.file_drop_here
import org.jetbrains.compose.resources.stringResource

/** Test tags of [SpreadsheetDropZone]. */
object SpreadsheetDropZoneTags {
    const val ZONE = "spreadsheet_drop_zone"
    const val CHOOSE = "spreadsheet_drop_zone_choose"
}

/**
 * SH-214 (reused by SH-217): where a spreadsheet comes in: a dashed area that takes a file dragged
 * onto it (the shared [fileDropTarget] of SH-233, checked against [spreadsheetDropSpec] like the
 * chooser) and a button opening the file chooser ([rememberSpreadsheetFilePicker]). While a file
 * is held over it the title says "Drop the file here"; a file of another type is refused with a
 * message under the button. Where nothing can be dragged in (iOS) it shows [chooseTitle] instead
 * of [dropTitle]. The texts come from the caller, so each import words them for its own files.
 */
@Composable
fun SpreadsheetDropZone(
    dropTitle: String,
    chooseTitle: String,
    hint: String?,
    chooseLabel: String,
    onFile: OnFilePicked,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    isReading: Boolean = false,
    readingLabel: String? = null
) {
    val onFileState = rememberUpdatedState(onFile)
    val picker = rememberSpreadsheetFilePicker { onFileState.value(it) }
    val drop = rememberFileDropState(spreadsheetDropSpec) { files -> onFileState.value(files.firstOrNull()?.let { PickedFile(it.name, it.bytes) }) }
    val dragging = drop.dragging
    val border = if (dragging) Gold else DeepNavy.copy(alpha = 0.35f)
    val dropsHere = enabled && supportsFileDrop
    Column(
        modifier = modifier
            .fillMaxWidth()
            .testTag(SpreadsheetDropZoneTags.ZONE)
            .semantics { contentDescription = if (dropsHere) dropTitle else chooseTitle }
            .fileDropTarget(drop, enabled = dropsHere)
            .background(if (dragging) GoldTintedWhite else androidx.compose.ui.graphics.Color.White.copy(alpha = 0.6f), RoundedCornerShape(16.dp))
            .drawBehind {
                drawRoundRect(
                    color = border,
                    cornerRadius = CornerRadius(16.dp.toPx()),
                    style = Stroke(width = (if (dragging) 3 else 2).dp.toPx(), pathEffect = PathEffect.dashPathEffect(floatArrayOf(10.dp.toPx(), 6.dp.toPx())))
                )
            }
            .padding(horizontal = 16.dp, vertical = 20.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        if (isReading) {
            CircularProgressIndicator(Modifier.size(28.dp), strokeWidth = 2.dp, color = DeepNavy)
            readingLabel?.let { Text(it, color = DeepNavy, textAlign = TextAlign.Center) }
            return@Column
        }
        Icon(Icons.Default.UploadFile, contentDescription = null, tint = if (dragging) Gold else DeepNavy, modifier = Modifier.size(36.dp))
        Text(
            when {
                dragging -> stringResource(Res.string.file_drop_here)
                dropsHere -> dropTitle
                else -> chooseTitle
            },
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.Bold,
            color = DeepNavy,
            textAlign = TextAlign.Center
        )
        hint?.let { Text(it, style = MaterialTheme.typography.bodySmall, color = DeepNavy.copy(alpha = 0.7f), textAlign = TextAlign.Center) }
        if (enabled) {
            OutlinedButton(onClick = picker::pick, modifier = Modifier.testTag(SpreadsheetDropZoneTags.CHOOSE)) {
                Text(chooseLabel, color = DeepNavy)
            }
        }
        drop.message?.let { FileDropMessage(it.asString(), onDismiss = { drop.message = null }) }
    }
}

/** SH-233: what the spreadsheet drop zone takes: the chooser's types and read limit, one file. */
val spreadsheetDropSpec = FileDropSpec(
    extensions = SPREADSHEET_FILE_EXTENSIONS,
    readLimit = PICK_READ_LIMIT,
    mimeTypes = setOf("text/csv", "text/tab-separated-values", "text/plain", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
)
